import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { cn } from "@/lib/utils/cn";
import { startCase } from "lodash";
import { Check } from "lucide-react";
import { useEffect, useRef } from "react";
import { useFormContext, type UseFormRegisterReturn } from "react-hook-form";

interface MultiCategorySelectFormFieldProps {
  items: Array<{
    category: string;
    items: Array<{ label: string; value: string; disabled?: boolean }>;
  }>;
  label?: string;
  emptyMessage?: string;
  isItemsError?: boolean;
  isItemsLoading?: boolean;
  onChange?: (value: string) => void;
  deferChangeUntilClosed?: boolean;
  open: boolean;
  placeholder?: string;
  register: UseFormRegisterReturn;
  setOpen: (open: boolean) => void;
  buttonClassName?: string;
  disabled?: boolean;
  selectedLabel?: string;
  actions?: Array<{ label: string; onSelect: () => void; disabled?: boolean }>;
  searchPlaceholder?: string;
  /** Use inside dialogs/slideovers to keep the list interactive without bricking the page on outside click */
  modalPopover?: boolean;
}

export const MultiCategorySelectFormField = ({
  items,
  label,
  emptyMessage,
  isItemsError,
  isItemsLoading,
  onChange,
  deferChangeUntilClosed = false,
  open,
  placeholder,
  register,
  setOpen,
  buttonClassName,
  disabled = false,
  selectedLabel,
  actions = [],
  searchPlaceholder,
  modalPopover = true,
}: MultiCategorySelectFormFieldProps) => {
  const form = useFormContext();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!modalPopover || !open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const targetNode = event.target as Node | null;
      if (
        !targetNode ||
        popoverContentRef.current?.contains(targetNode) ||
        triggerRef.current?.contains(targetNode)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
      setOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [modalPopover, open, setOpen]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "Enter") {
      event.preventDefault();
      setOpen(true);
    }
  };

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => (
        <FormItem>
          {label && (
            <div className="flex">
              <FormLabel>{label}</FormLabel>
              <FormMessage
                data-testid="error-message"
                className="grow text-right"
              />
            </div>
          )}
          <Popover modal={modalPopover} open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                ref={triggerRef}
                disabled={disabled}
                aria-expanded={open}
                className={cn(
                  "border-input group flex w-full items-center justify-between gap-2",
                  buttonClassName,
                  !field.value && "text-muted-foreground",
                )}
                onKeyDown={handleKeyDown}
              >
                {field.value
                  ? (items
                      .flatMap((category) => category.items)
                      .find((item) => item.value === field.value)?.label ??
                    selectedLabel)
                  : (placeholder ?? "Select an item...")}
                <SelectDropdownChevron />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              onCloseAutoFocus={(event) => {
                const action = pendingActionRef.current;
                pendingActionRef.current = null;
                if (action) {
                  event.preventDefault();
                  action();
                }
              }}
              ref={popoverContentRef}
              align="start"
              className="w-[var(--radix-popover-trigger-width)] max-w-[calc(100vw-2rem)] min-w-[200px] p-0"
              data-modal-popover={modalPopover ? "true" : undefined}
            >
              <Command
                value={
                  searchPlaceholder ? undefined : (field.value ?? undefined)
                }
                filter={
                  searchPlaceholder
                    ? (_value, search, keywords) => {
                        const normalizedSearch = search.trim().toLowerCase();
                        if (!normalizedSearch) return 1;

                        return keywords?.some((keyword) =>
                          keyword.toLowerCase().includes(normalizedSearch),
                        )
                          ? 1
                          : 0;
                      }
                    : undefined
                }
              >
                {searchPlaceholder && (
                  <CommandInput placeholder={searchPlaceholder} />
                )}
                <CommandList className="[&_[cmdk-group]:not([hidden])~[cmdk-group]:not([hidden])]:border-t">
                  {isItemsLoading && (
                    <CommandEmpty>
                      <div className="justify-self-center">
                        <LoadingSpinner />
                      </div>
                    </CommandEmpty>
                  )}
                  {isItemsError && (
                    <CommandEmpty>Unable to load items.</CommandEmpty>
                  )}
                  {!isItemsLoading && !isItemsError && (
                    <CommandEmpty>
                      {emptyMessage ?? "No item found."}
                    </CommandEmpty>
                  )}
                  {items.map((category) => (
                    <CommandGroup
                      key={category.category}
                      heading={startCase(category.category)}
                    >
                      {category.items.map((item) => (
                        <CommandItem
                          className="cursor-pointer justify-between"
                          key={item.value}
                          value={item.value}
                          keywords={[item.label]}
                          onSelect={(currentValue) => {
                            field.onChange(currentValue);
                            if (deferChangeUntilClosed) {
                              pendingActionRef.current = () =>
                                onChange?.(currentValue);
                            } else {
                              onChange?.(currentValue);
                            }
                            setOpen(false);
                            triggerRef.current?.focus();
                          }}
                          disabled={item.disabled}
                        >
                          {item.label}
                          <Check
                            className={cn(
                              "h-4 w-4 shrink-0",
                              field.value === item.value
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                  {actions.length > 0 && (
                    <CommandGroup>
                      {actions.map((action) => (
                        <CommandItem
                          key={action.label}
                          disabled={action.disabled}
                          onSelect={() => {
                            pendingActionRef.current = action.onSelect;
                            setOpen(false);
                          }}
                        >
                          {action.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </FormItem>
      )}
    />
  );
};
